import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useOutletContext } from 'react-router-dom'

import DataFormTitle from '@App/components/DataFormTitle/DataFormTitle'
import DrawModeToggles from '@App/components/DrawModeToggles/DrawModeToggles'
import TypeSelect from '@App/components/TypeSelect/TypeSelect'
import VisibilityToggles from '@App/components/VisibilityToggles/VisibilityToggles'
import { createMissionData, fetchMissionDataTypesByGroup } from '@services/data.service'

import BreedingSitesData from '../BreedingSitesData/BreedingSitesData'

import './breeding-sites-form.styl'

function BreedingSitesForm() {
  const navigate = useNavigate()
  const { map } = useOutletContext()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [breedingSiteType, setBreedingSiteType] = useState()
  const [drawnFeature, setDrawnFeature] = useState(null)
  const [visibility, setVisibility] = useState('private')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { activeProject } = useSelector(state => state.missionSetup)

  const { data: types } = useQuery({
    queryKey: ['types', 'breeding', activeProject?.id],
    queryFn: () => fetchMissionDataTypesByGroup('breeding', activeProject?.id),
  })

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!activeProject?.id || !breedingSiteType || !drawnFeature) return

    setIsSubmitting(true)

    const selectedType = types?.find(type => type.id === breedingSiteType)
    const source = selectedType?.dataSource?.[0] || 'custom'
    const geometry = drawnFeature.geometry || drawnFeature

    try {
      await createMissionData({
        type: 'Feature',
        id: name,
        geometry,
        properties: {
          id: name,
          mission: activeProject.id,
          dataType: breedingSiteType,
          name,
          description,
          source,
          private: visibility === 'private',
        },
      })
      queryClient.invalidateQueries({
        queryKey: ['breeding', activeProject.id],
        exact: false,
      })
      navigate(-1)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form
      className={`breeding-sites-form${isSubmitting ? ' breeding-sites-form--loading' : ''}`}
      onSubmit={onSubmit}
    >
      <DataFormTitle>Name</DataFormTitle>
      <div className="breeding-sites-form__input-field">
        <input type="text" value={name} onChange={e => setName(e.target.value)} />
      </div>
      <DataFormTitle>Description</DataFormTitle>
      <div className="breeding-sites-form__input-field">
        <textarea value={description} onChange={e => setDescription(e.target.value)} />
      </div>
      <DataFormTitle>Select data type</DataFormTitle>
      <div className="breeding-sites-form__input-field">
        <TypeSelect
          value={breedingSiteType}
          onChange={setBreedingSiteType}
          types={types}
          iconBasePath="/img/icons/types/breeding"
        />
      </div>
      <DataFormTitle>Data location</DataFormTitle>
      <div className="breeding-sites-form__input-field">
        <DrawModeToggles
          drawModes={types && types.find(t => t.id === breedingSiteType)?.drawMode}
          onDraw={setDrawnFeature}
          map={map}
        />
      </div>
      <DataFormTitle>Visibility</DataFormTitle>
      <VisibilityToggles
        value={visibility}
        onChange={setVisibility}
      />
      <button
        className="breeding-sites-form__submit"
        type="submit"
        disabled={!name || !breedingSiteType || !drawnFeature || isSubmitting}
      >
        {isSubmitting ? 'Submitting…' : 'Submit'}
      </button>
      <BreedingSitesData passive={true} />
    </form>
  )
}

export default BreedingSitesForm
